import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search, Building2, Users, ChevronRight, ArrowRight, Globe, Filter, X } from 'lucide-react';
import Navbar from '@/components/Navbar';
import MegaFooter from '@/components/MegaFooter';
import { ChatWidget } from '@/components/ChatWidget';

var API_URL = process.env.REACT_APP_BACKEND_URL || '';

function FeaturedComparisons({ pages }) {
  var vsPages = pages.filter(function(p) { return p.slug && p.slug.indexOf('channelloyalty-vs-') === 0; }).slice(0, 12);
  var bestPages = pages.filter(function(p) { return p.slug && (p.slug.indexOf('best-') === 0 || p.slug.indexOf('top-') === 0); }).slice(0, 6);
  
  if (vsPages.length === 0 && bestPages.length === 0) return null;

  return (
    <div data-testid="featured-comparisons">
      {vsPages.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <Globe size={16} className="text-orange-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Platform Comparisons</h2>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{vsPages.length}</span>
          </div>
          <ComparisonGrid items={vsPages} />
        </div>
      )}
      {bestPages.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <ChevronRight size={16} className="text-green-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">Best-In-Class Rankings</h2>
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{bestPages.length}</span>
          </div>
          <ComparisonGrid items={bestPages} />
        </div>
      )}
    </div>
  );
}

function ComparisonGrid({ items }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {items.map(function(page) {
        return (
          <Link
            key={page.slug}
            to={"/solutions/" + page.slug}
            className="group block p-4 rounded-xl bg-white border border-gray-100 hover:border-orange-200 hover:shadow-md transition-all"
            data-testid={"comparison-" + page.slug}
          >
            <h3 className="text-sm font-semibold text-gray-900 group-hover:text-orange-600 transition-colors leading-snug mb-1">{page.title}</h3>
            {page.description && (
              <p className="text-xs text-gray-500 line-clamp-2">{page.description}</p>
            )}
            <div className="flex items-center mt-2">
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-orange-50 text-orange-600">Comparison</span>
              <ChevronRight size={12} className="ml-auto text-gray-300 group-hover:text-orange-500 transition-colors" />
            </div>
          </Link>
        );
      })}
    </div>
  );
}

export default function SolutionsHubPage() {
  var dataState = useState(null);
  var hubData = dataState[0];
  var setHubData = dataState[1];

  var searchState = useState('');
  var search = searchState[0];
  var setSearch = searchState[1];

  var filterState = useState('all');
  var filterType = filterState[0];
  var setFilterType = filterState[1];

  var filterValueState = useState('');
  var filterValue = filterValueState[0];
  var setFilterValue = filterValueState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var searchParams = useSearchParams()[0];

  // Apply URL query params on mount
  useEffect(function() {
    var industryParam = searchParams.get('industry');
    var stakeholderParam = searchParams.get('stakeholder');
    if (industryParam) {
      setFilterType('industry');
      setFilterValue(industryParam);
    } else if (stakeholderParam) {
      setFilterType('stakeholder');
      setFilterValue(stakeholderParam);
    }
  }, [searchParams]);

  useEffect(function() {
    fetch(API_URL + '/api/seo/hub-data')
      .then(function(r) { return r.json(); })
      .then(function(data) { setHubData(data); setLoading(false); })
      .catch(function() { setLoading(false); });
  }, []);

  var allPages = useMemo(function() {
    if (!hubData) return [];
    var pages = [];
    var industries = Object.keys(hubData.by_industry || {});
    industries.forEach(function(ind) {
      (hubData.by_industry[ind] || []).forEach(function(p) {
        pages.push(p);
      });
    });
    // Dedupe by slug
    var seen = {};
    return pages.filter(function(p) {
      if (seen[p.slug]) return false;
      seen[p.slug] = true;
      return true;
    });
  }, [hubData]);

  var filteredPages = useMemo(function() {
    var pages = allPages;
    if (search) {
      var q = search.toLowerCase();
      pages = pages.filter(function(p) {
        return p.title.toLowerCase().indexOf(q) !== -1 || p.industry.toLowerCase().indexOf(q) !== -1 || p.stakeholder.toLowerCase().indexOf(q) !== -1;
      });
    }
    if (filterType === 'industry' && filterValue) {
      pages = pages.filter(function(p) { return p.industry === filterValue; });
    }
    if (filterType === 'stakeholder' && filterValue) {
      pages = pages.filter(function(p) { return p.stakeholder === filterValue; });
    }
    return pages;
  }, [allPages, search, filterType, filterValue]);

  var groupedView = useMemo(function() {
    if (filterType !== 'all' && filterValue) return null;
    if (search) return null;
    if (!hubData) return null;
    return hubData.by_industry;
  }, [hubData, filterType, filterValue, search]);

  function clearFilters() {
    setSearch('');
    setFilterType('all');
    setFilterValue('');
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="solutions-hub-page">
      <Helmet>
        <title>All Solutions - B2B Channel Loyalty Programs | ChannelLoyalty</title>
        <meta name="description" content={"Explore " + (hubData ? hubData.total : 500) + "+ B2B channel loyalty solutions across industries. Find dealer, distributor, retailer, and influencer loyalty programs for cement, FMCG, auto, paints, pharma and more."} />
      </Helmet>
      <Navbar />

      {/* Hero — clean landing page style */}
      <section className="bg-white pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase border border-[#7CC326]/30 text-[#7CC326] bg-[#7CC326]/5 mb-5">Solutions Library</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2C39B8] mb-4">
              All Loyalty Solutions
            </h1>
            <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto">
              {hubData ? hubData.total : '500'}+ enterprise-grade B2B channel loyalty programs across industries and stakeholder types
            </p>
          </div>

          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={function(e) { setSearch(e.target.value); }}
              placeholder="Search by industry, stakeholder, or keyword..."
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7CC326]/50 focus:border-[#7CC326] text-sm"
              data-testid="hub-search-input"
            />
          </div>
        </div>
      </section>

      {/* Featured Comparisons Section — hidden while searching/filtering so results appear immediately */}
      {!loading && hubData && !search && !filterValue && (
        <section className="max-w-7xl mx-auto px-4 pt-8 pb-4">
          <FeaturedComparisons pages={allPages} />
        </section>
      )}

      {/* Filters + Content */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        {/* Filter bar */}
        <div className="flex flex-wrap gap-2 items-center mb-8">
          <div className="flex items-center gap-1 text-sm text-gray-500 mr-2">
            <Filter size={14} />
            <span>Filter:</span>
          </div>

          {/* Industry pills */}
          {hubData && (hubData.industries || []).map(function(ind) {
            var isActive = filterType === 'industry' && filterValue === ind;
            return (
              <button
                key={ind}
                onClick={function() {
                  if (isActive) { clearFilters(); } else { setFilterType('industry'); setFilterValue(ind); }
                }}
                className={"px-3 py-1 rounded-full text-xs font-medium transition-all " + (isActive ? "bg-blue-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-blue-300")}
                data-testid={"filter-" + ind}
              >
                {ind}
              </button>
            );
          })}

          {/* Stakeholder pills */}
          {hubData && (hubData.stakeholders || []).map(function(sth) {
            var isActive = filterType === 'stakeholder' && filterValue === sth;
            return (
              <button
                key={sth}
                onClick={function() {
                  if (isActive) { clearFilters(); } else { setFilterType('stakeholder'); setFilterValue(sth); }
                }}
                className={"px-3 py-1 rounded-full text-xs font-medium transition-all " + (isActive ? "bg-[#2C39B8] text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-blue-300")}
                data-testid={"filter-" + sth}
              >
                {sth}
              </button>
            );
          })}

          {(search || filterValue) && (
            <button onClick={clearFilters} className="px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 flex items-center gap-1">
              <X size={12} /> Clear
            </button>
          )}

          <span className="ml-auto text-xs text-gray-400">{filteredPages.length} solutions</span>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Grouped by industry view */}
        {!loading && groupedView && !search && filterType === 'all' && (
          <div className="space-y-10">
            {Object.keys(groupedView).sort().map(function(industry) {
              var pages = groupedView[industry];
              return (
                <div key={industry} data-testid={"hub-section-" + industry}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Building2 size={16} className="text-blue-600" />
                    </div>
                    <h2 className="text-lg font-bold text-gray-900">{industry}</h2>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{pages.length}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {pages.map(function(page) {
                      return (
                        <Link
                          key={page.slug}
                          to={"/solutions/" + page.slug}
                          className="group block p-4 rounded-xl bg-white border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all"
                          data-testid={"hub-link-" + page.slug}
                        >
                          <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors leading-snug mb-1">{page.title}</h3>
                          {page.description && (
                            <p className="text-xs text-gray-500 line-clamp-2">{page.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            {page.stakeholder && page.stakeholder !== 'Multi-Stakeholder' && (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-[#2C39B8]">{page.stakeholder}</span>
                            )}
                            <ChevronRight size={12} className="ml-auto text-gray-300 group-hover:text-blue-500 transition-colors" />
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Flat filtered/searched view */}
        {!loading && (search || filterValue) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredPages.map(function(page) {
              return (
                <Link
                  key={page.slug}
                  to={"/solutions/" + page.slug}
                  className="group block p-4 rounded-xl bg-white border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all"
                >
                  <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors leading-snug mb-1">{page.title}</h3>
                  {page.description && (
                    <p className="text-xs text-gray-500 line-clamp-2">{page.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{page.industry}</span>
                    {page.stakeholder && page.stakeholder !== 'Multi-Stakeholder' && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-[#2C39B8]">{page.stakeholder}</span>
                    )}
                    <ChevronRight size={12} className="ml-auto text-gray-300 group-hover:text-blue-500 transition-colors" />
                  </div>
                </Link>
              );
            })}
            {filteredPages.length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-400">
                No solutions found. Try a different search.
              </div>
            )}
          </div>
        )}
      </section>

      <MegaFooter />
      <ChatWidget />
    </div>
  );
}
